/**
 * Google Authentication Utility with FedCM Support
 * Uses modern Google Identity Services (GIS) with Federated Credential Management
 */

const GOOGLE_CLIENT_ID = '979810507202-i17nil2pqft0bbuel6bf4t9eht71d09d.apps.googleusercontent.com';

// Check if FedCM is supported
const isFedCMSupported = () => {
  return 'IdentityCredential' in window && 'navigator.credentials' in window;
};

// Initialize Google Identity Services with FedCM
export const initializeGoogleAuth = () => {
  return new Promise((resolve, reject) => {
    if (window.google) {
      const config = {
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: isFedCMSupported() // Enable FedCM if supported
      };

      window.google.accounts.id.initialize(config);
      resolve();
    } else {
      reject(new Error('Google Identity Services not loaded'));
    }
  });
};

// Handle the credential response from Google
const handleCredentialResponse = (response) => {
  console.log('Credential response:', response);
  
  // Decode the JWT token to get user info
  const userInfo = parseJwt(response.credential);
  console.log('User info:', userInfo);
  
  // Store user info in localStorage for persistence
  localStorage.setItem('google_user', JSON.stringify(userInfo));
  localStorage.setItem('google_credential', response.credential);
  
  // Trigger a custom event for the component to listen to
  window.dispatchEvent(new CustomEvent('googleSignIn', { detail: userInfo }));
};

// Parse JWT token to extract user information
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
};

// Sign in with Google using FedCM or One Tap
export const signInWithGoogle = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.accounts.id) {
      // Try FedCM first if supported
      if (isFedCMSupported()) {
        console.log('Using FedCM for authentication');
        tryFedCMAuthentication().then(resolve).catch(() => {
          // Fallback to One Tap if FedCM fails
          console.log('FedCM failed, falling back to One Tap');
          tryOneTapAuthentication().then(resolve).catch(reject);
        });
      } else {
        // Use One Tap if FedCM is not supported
        console.log('FedCM not supported, using One Tap');
        tryOneTapAuthentication().then(resolve).catch(reject);
      }
    } else {
      reject(new Error('Google Identity Services not initialized'));
    }
  });
};

// Try FedCM authentication
const tryFedCMAuthentication = () => {
  return new Promise((resolve, reject) => {
    if (!isFedCMSupported()) {
      reject(new Error('FedCM not supported'));
      return;
    }

    navigator.credentials.get({
      identity: {
        providers: [{
          configURL: 'https://accounts.google.com/.well-known/web_identity',
          clientId: GOOGLE_CLIENT_ID
        }]
      }
    }).then(credential => {
      if (credential && credential.token) {
        const userInfo = parseJwt(credential.token);
        localStorage.setItem('google_user', JSON.stringify(userInfo));
        localStorage.setItem('google_credential', credential.token);
        window.dispatchEvent(new CustomEvent('googleSignIn', { detail: userInfo }));
        resolve(userInfo);
      } else {
        reject(new Error('No credential received from FedCM'));
      }
    }).catch(error => {
      console.error('FedCM authentication failed:', error);
      reject(error);
    });
  });
};

// Try One Tap authentication
const tryOneTapAuthentication = () => {
  return new Promise((resolve, reject) => {
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // Fallback to popup if One Tap is not available
        window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'email profile',
          callback: (response) => {
            if (response.error) {
              reject(new Error(response.error));
            } else {
              // Get user info using the access token
              fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`)
                .then(res => res.json())
                .then(userInfo => {
                  localStorage.setItem('google_user', JSON.stringify(userInfo));
                  localStorage.setItem('google_access_token', response.access_token);
                  window.dispatchEvent(new CustomEvent('googleSignIn', { detail: userInfo }));
                  resolve(userInfo);
                })
                .catch(reject);
            }
          }
        }).requestAccessToken();
      }
    });
  });
};

// Sign out from Google
export const signOutFromGoogle = () => {
  return new Promise((resolve, reject) => {
    try {
      // Clear stored data
      localStorage.removeItem('google_user');
      localStorage.removeItem('google_credential');
      localStorage.removeItem('google_access_token');
      
      // Sign out from Google
      if (window.google && window.google.accounts.id) {
        window.google.accounts.id.disableAutoSelect();
      }
      
      // Trigger sign out event
      window.dispatchEvent(new CustomEvent('googleSignOut'));
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

// Get current user from localStorage
export const getCurrentUser = () => {
  const userData = localStorage.getItem('google_user');
  return userData ? JSON.parse(userData) : null;
};

// Get user profile (alias for getCurrentUser for compatibility)
export const getUserProfile = () => {
  return getCurrentUser();
};

// Check if user is signed in
export const isSignedIn = () => {
  const user = getCurrentUser();
  return user !== null;
};

// Listen for auth state changes
export const onAuthStateChanged = (callback) => {
  window.addEventListener('googleSignIn', (event) => {
    callback(true, event.detail);
  });
  
  window.addEventListener('googleSignOut', () => {
    callback(false, null);
  });
};

// Check FedCM support
export const checkFedCMSupport = () => {
  return isFedCMSupported();
};

// Render the Google Sign-In button with FedCM support
export const renderGoogleButton = (elementId, options = {}) => {
  if (window.google && window.google.accounts.id) {
    const buttonOptions = {
      theme: 'outline',
      size: 'large',
      type: 'standard',
      shape: 'rectangular',
      text: 'signin_with',
      use_fedcm_for_prompt: isFedCMSupported(),
      ...options
    };

    window.google.accounts.id.renderButton(
      document.getElementById(elementId),
      buttonOptions
    );
  }
};
